import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import RocketLaunchTwoToneIcon from '@mui/icons-material/RocketLaunchTwoTone';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

/**
 * Shown when the service worker has a new build waiting.
 *
 * It is a CENTRED MODAL rather than the corner toast this replaced: on a shop floor a toast in
 * the corner of a 15" till screen is missed for days, so staff keep running an old build and
 * report bugs that were fixed weeks ago.
 *
 * It is deliberately DISMISSIBLE. This is a point-of-sale: updating reloads the page, and a
 * modal that cannot be dismissed would trap a cashier who is halfway through ringing up a
 * customer with a queue behind them. "Later" is a first-class choice, and the prompt returns on
 * the next page load.
 */
export interface UpdateAvailableDialogProps {
  open: boolean;
  version?: string | null;
  onUpdate: () => void;
  onDismiss: () => void;
}

export default function UpdateAvailableDialog({
  open,
  version,
  onUpdate,
  onDismiss
}: UpdateAvailableDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [applying, setApplying] = useState(false);

  // If the dialog is re-opened (dismissed, then a later build arrives) the button must not still
  // be stuck in its loading state from the previous round.
  useEffect(() => {
    if (open) setApplying(false);
  }, [open]);

  const handleUpdate = () => {
    setApplying(true);
    onUpdate();
  };

  const highlights = [
    'Latest fixes and improvements',
    'Takes a few seconds — your data is safe',
    'You will be signed in as normal afterwards'
  ];

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // An accidental click outside should not dismiss an update prompt that staff may only
        // see once; the explicit buttons decide. Escape still works as a way out.
        if (reason === 'backdropClick') return;
        if (!applying) onDismiss();
      }}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={220}
      aria-labelledby="app-update-title"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.28)}`
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: alpha(theme.palette.common.black, 0.55),
          backdropFilter: 'blur(3px)'
        }
      }}
    >
      {/* Banner. Uses the theme's primary colour so it follows a re-brand and both themes. */}
      <Box
        sx={{
          position: 'relative',
          px: 3,
          pt: 4,
          pb: 3,
          textAlign: 'center',
          color: theme.palette.primary.contrastText,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
        }}
      >
        {/* Soft halo behind the icon — decorative only. */}
        <Box
          sx={{
            width: 76,
            height: 76,
            mx: 'auto',
            mb: 1.5,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.common.white, 0.18),
            border: `1px solid ${alpha(theme.palette.common.white, 0.28)}`
          }}
        >
          <RocketLaunchTwoToneIcon sx={{ fontSize: 40 }} />
        </Box>

        <Typography id="app-update-title" variant="h4" sx={{ fontWeight: 700, color: 'inherit' }}>
          Update available
        </Typography>

        {version ? (
          <Box
            sx={{
              display: 'inline-block',
              mt: 1.25,
              px: 1.5,
              py: 0.4,
              borderRadius: 5,
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 0.2,
              bgcolor: alpha(theme.palette.common.white, 0.2)
            }}
          >
            Version {version}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ px: 3, pt: 3, pb: 3 }}>
        <Typography
          variant="body1"
          sx={{ color: 'text.secondary', textAlign: 'center', mb: 2.5 }}
        >
          A newer version of Sum Cloud POS is ready to install. Updating reloads the page, so
          finish what you are doing first if you are in the middle of a sale.
        </Typography>

        <Stack spacing={1.25} sx={{ mb: 3 }}>
          {highlights.map((line) => (
            <Stack key={line} direction="row" spacing={1.25} alignItems="center">
              <CheckCircleRoundedIcon
                sx={{ fontSize: 19, color: theme.palette.success.main, flexShrink: 0 }}
              />
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {line}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack spacing={1.25}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={handleUpdate}
            disabled={applying}
            startIcon={
              applying ? <CircularProgress size={18} color="inherit" /> : null
            }
            sx={{ borderRadius: 2, py: 1.25, fontWeight: 700, textTransform: 'none', fontSize: 15 }}
          >
            {applying ? 'Updating…' : 'Update now'}
          </Button>

          <Button
            fullWidth
            size="large"
            variant="text"
            onClick={onDismiss}
            disabled={applying}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary'
            }}
          >
            Later
          </Button>
        </Stack>

        <Typography
          variant="caption"
          sx={{ display: 'block', mt: 2, textAlign: 'center', color: 'text.disabled' }}
        >
          If you choose Later, you will be reminded next time you open the app.
        </Typography>
      </Box>
    </Dialog>
  );
}
